import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { findUtType, utUnitOf } from '../constants';
import { AtcDivsCdSum } from '../types';

/**
 * 분석 Report 탭의 UT 항목 목록 — 리포트에 저장된 데이터(SUM_M)가 아니라 **공통코드**가 기준이다.
 *
 * 저장된 행에서 목록을 만들면 리포트마다 항목이 달라 보이고, 데이터가 없는 항목은 아예
 * 나타나지 않는다. 항목 자체는 마스터 성격이라 공통코드에서 가져온다.
 */

/**
 * 전기 UT 항목 = 생산공정(PRDN_PROC_CD) 중 **UT 항목 적용대상(optValCtn1='Y')**.
 * UtMatrixRequestPage / UtMatrixStandardCodePage가 쓰는 조회 조건과 동일하다.
 */
export const ELEC_ITEM_GROUP_CD = 'PRDN_PROC_CD';
export const ELEC_ITEM_TARGET_FLAG = 'Y';

/** 기계 UT 항목 카테고리 */
export const MECH_ITEM_GROUP_CD = 'MECH_UT_CTGR_CD';

/** 전기는 표 전체를 MW로 집계한다 (toRptSumItems의 기본값과 동일) */
const ELEC_UNIT = 'MW';

export interface UtItemCode {
  atcDivsCd: AtcDivsCdSum;
  cd: string;
  nm: string;
  /**
   * 공통코드에 단위 칼럼이 없어 FE 상수에서 붙인다.
   * 기계는 constants.ts의 UT_TYPES label(`CDA [Nl/min]`)이 유일한 단위 출처다.
   */
  unit: string;
}

export interface UtItemCodes {
  elec: UtItemCode[];
  mech: UtItemCode[];
}

export const EMPTY_UT_ITEM_CODES: UtItemCodes = { elec: [], mech: [] };

const toItemCodes = (
  rows: Code[] | null,
  atcDivsCd: AtcDivsCdSum,
  unitOf: (cd: string) => string
): UtItemCode[] =>
  (rows ?? [])
    .filter((r) => !!r?.cmnCd)
    .map((r) => {
      const cd = String(r.cmnCd);
      return { atcDivsCd, cd, nm: r.cmnCdNm || cd, unit: unitOf(cd) };
    });

/**
 * 항목코드 비교 — 기계는 화면키('Steam')와 DB코드('ST')가 섞여 들어올 수 있어
 * findUtType으로 정규화해서 대조한다(UtMatrixComparisonReportList와 같은 이유).
 */
export const isSameUtItemCode = (a: string, b: string, isMech: boolean): boolean => {
  if (!a || !b) return false;
  if (a.toUpperCase() === b.toUpperCase()) return true;
  if (!isMech) return false;
  const ta = findUtType(a);
  const tb = findUtType(b);
  return !!ta && !!tb && ta.key === tb.key;
};

/**
 * 전기/기계 UT 항목 공통코드 조회.
 *
 * 한쪽 그룹이 아직 등록되지 않았거나 조회에 실패해도 다른 쪽은 살린다 — 실패한 쪽은
 * 빈 배열이 되고, 그 구분의 항목 버튼은 표시되지 않는다(저장된 데이터로 대체하지 않는다).
 */
export const fetchUtItemCodes = async (): Promise<UtItemCodes> => {
  const [elecRows, mechRows] = await Promise.all([
    getCommonCodeNamesCondition({
      cmnGrCd: ELEC_ITEM_GROUP_CD,
      optValCtn1: ELEC_ITEM_TARGET_FLAG,
    }).catch(() => [] as Code[]),
    getCommonCodeNames(MECH_ITEM_GROUP_CD).catch(() => [] as Code[]),
  ]);

  return {
    elec: toItemCodes(elecRows, 'ELEC', () => ELEC_UNIT),
    mech: toItemCodes(mechRows, 'MECH', (cd) => utUnitOf(cd)),
  };
};
